import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import store from 'store';
import { paths } from 'frameworks_drivers/routes';
import { TrackerUtil } from 'tracking/TrackerUtil';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardSteps, WizardStepsContainer } from 'presentation/components/WizardSteps';
import PageDescription from 'presentation/components/PageDescription';
import {
  Address,
  setInvoiceAddress
} from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import { useNavigate } from 'react-router-dom';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import InvoiceAddressForm from './components/InvoiceAddressForm';

const InvoiceAddressPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prevPath = paths.ContactDetails;

  // Standard-Formularsteuerung ohne AssistedAddressSearchContext
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      zip: '',
      city: '',
      street: '',
      houseNr: ''
    }
  });

  // Werte aus Store wiederherstellen
  const restoreFormFields = useCallback(
    (storedAddress: Address) => {
      setValue('zip', storedAddress.zip);
      setValue('city', storedAddress.city);
      setValue('street', storedAddress.street);
      setValue('houseNr', storedAddress.houseNumber);
    },
    [setValue]
  );

  useEffect(() => {
    if (
      !DISABLE_FLOW_DEEP_LINKING_PROTECTION &&
      store.getState().onboardingInfo.contactDetails.lname === ''
    ) {
      navigate(prevPath, { replace: true });
    }

    const storedAddress = store.getState().onboardingInfo.invoiceAddress;
    restoreFormFields(storedAddress);
  }, [prevPath, navigate, restoreFormFields]);

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  const onSubmit = handleSubmit(({ street, houseNr, zip, city }, e) => {
    e?.preventDefault();
    TrackerUtil.trackNextBtnClick();

    store.dispatch(setInvoiceAddress({ street, houseNumber: houseNr, zip, city }));
    navigate(paths.HouseAddress);
  });

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer>
        <WizardSteps currentStep={2} totalSteps={4} />
      </WizardStepsContainer>

      <TitleWithPrevPageLink
        onPrevClick={handlePrevBtnClicked}
        title={t('pageInvoiceAddressTitle')}
      />

      <PageDescription indented>{t('pageInvoiceAddressDescription')}</PageDescription>

      <InvoiceAddressForm
        formControl={control}
        errors={errors}
        setValue={setValue}
        onSubmit={onSubmit}
      />
    </Container>
  );
};

export default InvoiceAddressPage;
